import { QuestionOutlined } from "@ant-design/icons"
import { App, Button, Card, Empty, Flex, Form } from "antd"
import React, { useEffect } from "react"
import isEqual from "react-fast-compare"

import {
  type ProjectTypeAddPhaseFormData,
  type ProjectTypeTemplate,
  type ProjectTypeTemplateChecklistItemPrimitive,
  type ProjectTypeTemplatePhase
} from "../../types/project-type.types.ts"
import { ProjectTypeCard } from "./ProjectTypeCard.tsx"
import { ProjectTypeMilestone } from "./ProjectTypeMilestone.tsx"
import { ProjectTypeMilestoneCheckList } from "./ProjectTypeMilestoneCheckList.tsx"
import { ProjectTypeMilestoneHeader } from "./ProjectTypeMilestoneHeader.tsx"
import { ProjectTypeMilestones } from "./ProjectTypeMilestones/ProjectTypeMilestones.tsx"

export interface ProjectTypeFormData {
  projectType: {
    name: string
    description?: string
    is_active?: boolean
    phases?: ProjectTypeTemplatePhase[]
  }
  phase: {
    id: string
    phase_id: string
    name: string
    description: string
    order: number
    default_budget?: number
    default_duration_days: number
    checklist_items_primitive?: ProjectTypeTemplateChecklistItemPrimitive[]
  }
}

interface ProjectTypeProps {
  selectedPhase: ProjectTypeTemplatePhase | null
  projectType: ProjectTypeTemplate
  saving: boolean
  createAllowed?: boolean
  deleteAllowed?: boolean
  //
  onDeleteProjectType: () => void
  onCopyPhase: () => void
  onDeletePhase: () => void
  onSelectPhase: (phase: ProjectTypeTemplatePhase) => void
  onAddPhase: (data: ProjectTypeAddPhaseFormData) => void
  onSubmit: (data: ProjectTypeFormData) => void
}

export function ProjectType({
  selectedPhase,
  projectType,
  saving,
  createAllowed,
  deleteAllowed,
  onDeleteProjectType,
  onCopyPhase,
  onDeletePhase,
  onSelectPhase,
  onAddPhase,
  onSubmit
}: ProjectTypeProps) {
  const { modal } = App.useApp()
  const [form] = Form.useForm<ProjectTypeFormData>()

  const onSelectPhaseHandler = (phase: ProjectTypeTemplatePhase) => {
    const currentPhase = form.getFieldValue("phase")
    const isChanged = !isEqual(currentPhase, selectedPhase)

    const onOk = () => {
      onSelectPhase(phase)
      form.setFieldValue("phase", phase)
    }

    if (isChanged) {
      modal.confirm({
        centered: true,
        icon: <QuestionOutlined />,
        title:
          "You have unsaved changes. Switch to another milestone without saving?",
        cancelText: "Cancel",
        okText: "Confirm",
        okButtonProps: { danger: true },
        onOk
      })
    } else {
      onOk()
    }
  }

  useEffect(() => {
    form.setFieldValue("phase", selectedPhase)
  }, [selectedPhase])

  return (
    <Form
      form={form}
      clearOnDestroy
      onFinish={onSubmit}
      layout="vertical"
      className={"flex-1 flex gap-3 flex-col with-default-error size-full"}
      validateTrigger={["onSubmit", "onChange"]}
      disabled={saving}
      initialValues={{
        projectType: projectType,
        phase: selectedPhase
      }}
    >
      <Flex flex={1} gap={"middle"} vertical>
        <Flex vertical className={"size-full lg:flex-row gap-3"}>
          <div
            className={"flex gap-3 flex-col h-fit lg:w-1/3 2xl:1/3  shrink-0"}
          >
            <ProjectTypeCard
              deleteAllowed={deleteAllowed}
              onDeleteClick={onDeleteProjectType}
            />

            <ProjectTypeMilestones
              form={form}
              selectedPhaseId={selectedPhase?.id}
              phases={projectType?.phases}
              disabled={saving}
              onAddPhase={onAddPhase}
              createAllowed={createAllowed}
              onSelectPhase={onSelectPhaseHandler}
            />
          </div>
          <Card classNames={{ body: "p-0" }} className={"flex-1 h-fit"}>
            {selectedPhase ? (
              <>
                <ProjectTypeMilestoneHeader
                  saving={saving}
                  copyAllowed={createAllowed}
                  deleteAllowed={deleteAllowed}
                  onCopyPhase={onCopyPhase}
                  onDeletePhase={onDeletePhase}
                />
                <Flex vertical gap={"small"} className={"p-2"}>
                  <ProjectTypeMilestone />
                  <ProjectTypeMilestoneCheckList form={form} />
                </Flex>
              </>
            ) : (
              <Flex className={"p-3 "} justify={"center"} align={"center"}>
                <Empty description={"No milestone selected"} />
              </Flex>
            )}
          </Card>
        </Flex>

        <Flex
          gap={"small"}
          justify={"end"}
          className={
            "sticky bottom-0 border-t border-border bg-background pt-3"
          }
        >
          <Button disabled>Cancel</Button>
          <Button htmlType="submit" type={"primary"}>
            Save
          </Button>
        </Flex>
      </Flex>
    </Form>
  )
}
